import {
  Col,
  Flex,
  Form,
  Input,
  Paragraph,
  Row,
  Switch,
  type FormInstance,
} from "@ir/ui";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import { useCallback, useEffect, useMemo } from "react";
import { createEmailFormatValidator } from "./userEmailValidation";

export interface UserAddEditFormValues {
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
}

interface UsersAddEditFormProps {
  form: FormInstance<UserAddEditFormValues>;
  mode: "create" | "edit";
  initialValues: UserAddEditFormValues;
  onSubmit: (values: UserAddEditFormValues) => Promise<void> | void;
  onFormValidityChange?: (isSubmittable: boolean) => void;
}

export const UsersAddEditForm = ({
  form,
  mode,
  initialValues,
  onSubmit,
  onFormValidityChange,
}: UsersAddEditFormProps) => {
  const emailDisabled = useMemo(() => mode === "edit", [mode]);

  const updateFormValidity = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some((field) => (field.errors?.length ?? 0) > 0);

    const values = form.getFieldsValue();
    const firstName = values.firstName?.trim() ?? "";
    const lastName = values.lastName?.trim() ?? "";
    const email = values.email?.trim() ?? "";

    const hasRequiredValues =
      firstName !== "" && lastName !== "" && email !== "";

    const isDirty =
      mode === "create"
        ? true
        : firstName !== (initialValues.firstName?.trim() ?? "") ||
          lastName !== (initialValues.lastName?.trim() ?? "") ||
          Boolean(values.isActive) !== Boolean(initialValues.isActive);

    onFormValidityChange?.(hasRequiredValues && !hasErrors && isDirty);
  }, [form, initialValues, mode, onFormValidityChange]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
    updateFormValidity();
  }, [form, initialValues, updateFormValidity]);

  const nameRules = useMemo(
    () => [
      { required: true, message: "First name is required" },
      {
        max: 100,
        message: "First name must be less than or equal to 100 characters.",
      },
    ],
    [],
  );

  const lastNameRules = useMemo(
    () => [
      { required: true, message: "Last name is required" },
      {
        max: 100,
        message: "Last name must be less than or equal to 100 characters.",
      },
    ],
    [],
  );

  const emailRules = useMemo(
    () => [
      { required: true, message: "Email is required" },
      {
        max: 254,
        message: "Email must be less than or equal to 254 characters.",
      },
      { validator: createEmailFormatValidator() },
    ],
    [],
  );

  return (
    <ErrorBoundary>
      <Form
        form={form}
        onFinish={onSubmit}
        onFieldsChange={updateFormValidity}
        className="users-add-edit"
      >
        <Row>
          <Col span={6}>
            <Form.Item
              name="firstName"
              label="First Name"
              validateTrigger={["onChange", "onBlur"]}
              rules={nameRules}
            >
              <Input placeholder="First name" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="lastName"
              label="Last Name"
              validateTrigger={["onChange", "onBlur"]}
              rules={lastNameRules}
            >
              <Input placeholder="Last name" />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={11}>
            <Form.Item
              name="email"
              label="Email"
              validateTrigger={["onChange", "onBlur"]}
              rules={emailRules}
              helpText={
                emailDisabled
                  ? "Once email linked with a user, it cannot be changed."
                  : undefined
              }
            >
              <Input placeholder="Email" isDisabled={emailDisabled} />
            </Form.Item>
          </Col>
          <Col span={1}>
            <Flex
              align="start"
              gap={1}
              className="status-switch"
              direction="column"
            >
              <Paragraph className="m-0 custom-label">Status</Paragraph>
              <Form.Item name="isActive" valuePropName="isSelected" noStyle>
                <Switch
                  className="p-0 mt-1"
                  size="small"
                  aria-label="User active status"
                />
              </Form.Item>
            </Flex>
          </Col>
        </Row>
      </Form>
    </ErrorBoundary>
  );
};
